import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CONTACT_INFO, TESTIMONIALS } from "../data";
import ContactForm from "../components/ContactForm";
import { getImageAlt } from "../lib/imageMeta";
import { applyPageMeta, PAGE_SEO_CONFIG, DEFAULT_OG_IMAGE } from "../lib/seoMeta";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null); // For lightbox

  useEffect(() => {
    setIsLoading(true);
    setService(null);
    setGallery(null);

    // Fetch service by slug
    fetch(`https://api.dev.auxo.dk/public/by-slug/${slug}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Service not found");
        }
        return res.json();
      })
      .then((data) => {
        setService(data);
        
        // Set metadata matching Google SERP limits (max 512px title, max 970px description)
        const customMeta = PAGE_SEO_CONFIG[slug];
        if (customMeta) {
          applyPageMeta(customMeta);
        } else {
          applyPageMeta({
            title: `${data.title} | JG Mesterbyg`,
            description: data.description || `Få professionel rådgivning og udførelse af ${data.title.toLowerCase()} hos JG Mesterbyg på Mors. Få et uforpligtende tilbud.`,
            canonical: `/ydelser/${slug}`,
            image: Array.isArray(data.image) && data.image[0] ? data.image[0] : DEFAULT_OG_IMAGE,
          });
        }

        // Fetch corresponding gallery by slug (e.g. galleri-renovering)
        return fetch(`https://api.dev.auxo.dk/public/by-slug/galleri-${slug}`);
      })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        return null;
      })
      .then((galleryData) => {
        if (galleryData) {
          setGallery(galleryData);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading service detail:", err);
        setIsLoading(false);
        // Redirect to 404 if service is not found
        navigate("/404");
      });
  }, [slug, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-neutral-900">
        <i className="fa-solid fa-spinner animate-spin text-4xl text-yellow-500" />
        <p className="font-semibold text-neutral-600">Henter detaljer for ydelsen...</p>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="bg-white text-neutral-900">
      {/* Service Header / Hero */}
      <section className="relative bg-neutral-900 text-white py-16 lg:py-24 logo-line-container">
        <div className="logo-line-diagonal top-1/3 left-0 opacity-20" />
        <div className="logo-line-horizontal bottom-0 left-0 opacity-25" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 text-sm font-bold transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-arrow-left" />
                Tilbage til forsiden
              </Link>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-yellow-500 text-neutral-950 flex items-center justify-center text-2xl shadow-lg shrink-0">
                  <i className={`fa-solid ${service.icon || "fa-hammer"}`} />
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                  {service.title}
                </h1>
              </div>
              <p className="text-lg text-neutral-300 leading-relaxed">
                {service.description}
              </p>
              
              {/* Feature Highlights Grid in Hero */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                {slug === "renovering" ? (
                  <>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-hammer text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Total & delrenovering</span>
                      <span className="text-[10px] text-neutral-400">Fra rum til hel bolig</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-compass-drafting text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Gennemtænkt plan</span>
                      <span className="text-[10px] text-neutral-400">Vægge, lofter & gulve</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-users-gear text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Faglig koordinering</span>
                      <span className="text-[10px] text-neutral-400">Tømrer, el & VVS</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-file-signature text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Skriftligt tilbud</span>
                      <span className="text-[10px] text-neutral-400">Faste klare rammer</span>
                    </div>
                  </>
                ) : slug === "nyt-tag" ? (
                  <>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-magnifying-glass text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Spær & tagflade</span>
                      <span className="text-[10px] text-neutral-400">Grundig gennemgang</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-layer-group text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Kvalitetsmaterialer</span>
                      <span className="text-[10px] text-neutral-400">Tegl, skifer, stål mm.</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-shield-halved text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Undertag & lægter</span>
                      <span className="text-[10px] text-neutral-400">Komplet tæt løsning</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-file-signature text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Skriftligt tilbud</span>
                      <span className="text-[10px] text-neutral-400">Faste klare rammer</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-ruler-combined text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Præcis opmåling</span>
                      <span className="text-[10px] text-neutral-400">Perfekt pasform</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-layer-group text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Kvalitetsmaterialer</span>
                      <span className="text-[10px] text-neutral-400">Træ, træ/alu & plast</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-shield-halved text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Tætning & isolering</span>
                      <span className="text-[10px] text-neutral-400">Mod træk og kulde</span>
                    </div>
                    <div className="bg-neutral-800/80 border border-neutral-700/80 rounded-xl p-3 text-center">
                      <i className="fa-solid fa-file-signature text-yellow-500 text-lg mb-1 block" />
                      <span className="text-xs font-bold text-neutral-200 block">Skriftligt tilbud</span>
                      <span className="text-[10px] text-neutral-400">Faste klare rammer</span>
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-neutral-800 text-neutral-200 border border-neutral-700">
                  <i className="fa-solid fa-location-dot text-yellow-500" /> Mors & Erslev
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-neutral-800 text-neutral-200 border border-neutral-700">
                  <i className="fa-solid fa-location-dot text-yellow-500" /> Skive & omegn
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-neutral-800 text-neutral-200 border border-neutral-700">
                  <i className="fa-solid fa-location-dot text-yellow-500" /> Thisted & Nykøbing
                </span>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              {/* Background glow behind form */}
              <div className="absolute inset-0 bg-yellow-500/10 blur-3xl rounded-full -z-10" />
              <ContactForm source={`Ydelsesside Formular: ${service.title}`} buttonText="Få uforpligtende tilbud" />
            </div>
          </div>
        </div>
      </section>

      {/* Service Content & Gallery */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Main Content Column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Rich Text Content */}
              {slug === "udskiftning-af-vinduer-og-doere" ? (
                <div className="space-y-8">
                  {/* Boks 1: Intro / Nøglebudskab */}
                  <div className="bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-800 text-white p-7 sm:p-9 rounded-3xl shadow-xl border border-neutral-700/80 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-yellow-500/10 rounded-bl-full pointer-events-none blur-xl" />
                    <div className="relative z-10 space-y-4">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <i className="fa-solid fa-house-chimney-window" />
                        <span>Kvalitetsløsninger til boligen</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                        Vinduer og døre, der passer til dit hus og din hverdag
                      </h2>
                      <p className="text-neutral-200 text-base sm:text-lg leading-relaxed">
                        Trækker det fra vinduerne, eller er terrassedøren svær at lukke? Hos{" "}
                        <Link
                          to="/"
                          className="text-yellow-400 hover:text-yellow-300 font-bold underline underline-offset-4 decoration-yellow-400/60 hover:decoration-yellow-300 transition-colors"
                          title="Gå til forsiden for JG Mesterbyg"
                        >
                          JG Mesterbyg
                        </Link>{" "}
                        hjælper vi med vinduer og døre i Skive og omegn. Fra vores base i Erslev på Mors kører vi også til Thisted og Nykøbing Mors med omegn. Vi står for opmåling og montering, uanset om du skal have skiftet ét element eller flere i huset.
                      </p>
                      <div className="pt-2 flex flex-wrap gap-2 text-xs text-neutral-300">
                        <span className="bg-neutral-800/90 border border-neutral-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <i className="fa-solid fa-check text-yellow-400" /> Mors & Erslev
                        </span>
                        <span className="bg-neutral-800/90 border border-neutral-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <i className="fa-solid fa-check text-yellow-400" /> Skive & omegn
                        </span>
                        <span className="bg-neutral-800/90 border border-neutral-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <i className="fa-solid fa-check text-yellow-400" /> Thisted & Nykøbing Mors
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Boks 2: Hvornår er det tid til nye vinduer og døre? */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-calendar-check" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Tilstandsvurdering</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Hvornår er det tid til nye vinduer og døre?
                        </h2>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-1">
                      <div className="bg-white border border-neutral-200/90 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="flex items-center gap-2.5 text-neutral-900 font-bold text-base">
                          <i className="fa-solid fa-triangle-exclamation text-amber-500" />
                          <span>Typiske tegn på nedbrud</span>
                        </div>
                        <p className="text-neutral-600 text-sm leading-relaxed">
                          Udskiftning af vinduer og døre kan være relevant, hvis rammerne er nedbrudte, ruden er punkteret, eller elementerne ikke længere slutter tæt. Det kan give træk og gøre det svært at holde en behagelig temperatur. Vi ser på tilstanden og hjælper dig med at afklare, hvor der er behov for nye elementer.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200/90 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="flex items-center gap-2.5 text-neutral-900 font-bold text-base">
                          <i className="fa-solid fa-magnifying-glass text-yellow-600" />
                          <span>Faglig vurdering før udskiftning</span>
                        </div>
                        <p className="text-neutral-600 text-sm leading-relaxed">
                          Træk betyder ikke altid, at hele vinduet skal skiftes. Problemet kan også ligge i tætninger eller fuger. Derfor vurderer vi opgaven, før omfanget aftales. På den måde tager vi udgangspunkt i elementernes tilstand og ikke blot i deres alder.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 3: Materialeguide & Husets stil */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-palette" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Materialer & Æstetik</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Hvilke vinduer og døre passer til husets stil?
                        </h2>
                      </div>
                    </div>

                    <p className="text-neutral-700 text-base leading-relaxed">
                      Skal du have nye vinduer og døre i Thisted og omegn, hjælper vi dig med at vælge materialer og et udtryk, der passer til huset. Se på farver, sprosser og måden, elementerne åbner på. Placeringen af greb og dørens åbneretning har også betydning, når løsningen skal fungere i en travl hverdag.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                      <div className="bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500/50 transition-all flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center text-lg mb-3">
                            <i className="fa-solid fa-tree" />
                          </div>
                          <h3 className="font-bold text-neutral-900 text-lg mb-2">Træ</h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Træ passer godt til mange hustyper, men kræver løbende pleje af overfladen.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
                          <i className="fa-solid fa-paint-roller text-yellow-600" /> Klassisk & naturligt
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500/50 transition-all flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3">
                            <i className="fa-solid fa-shield" />
                          </div>
                          <h3 className="font-bold text-neutral-900 text-lg mb-2">Træ med aluminium</h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Træ med aluminium udvendigt mindsker behovet for udvendig maling og bevarer træets udtryk inde.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
                          <i className="fa-solid fa-star text-yellow-600" /> Populært & robust
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500/50 transition-all flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3">
                            <i className="fa-solid fa-soap" />
                          </div>
                          <h3 className="font-bold text-neutral-900 text-lg mb-2">Plast</h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Plast kræver ikke maling, men skal stadig rengøres og have beslag og tætninger efterset.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
                          <i className="fa-solid fa-broom text-yellow-600" /> Nem vedligeholdelse
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boks 4: Glasvalg & Ventilation */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-shield-halved" />
                        </div>
                        <h2 className="text-lg sm:text-xl font-bold text-neutral-900">
                          Glasvalget er vigtigt for dine nye vinduer
                        </h2>
                      </div>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        Glasset er vigtigt for både varmetab og oplevelsen tæt ved vinduet. Trelags energiglas er en mulighed, men valget skal passe til huset og de gældende krav. Har du gener fra trafikstøj, bør det indgå i valget af glas. Antallet af glaslag siger ikke alene, hvor godt løsningen dæmper lyd.
                      </p>
                    </div>

                    <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-wind" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-neutral-900">
                          Husk god ventilation
                        </h3>
                      </div>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        Nye, tætte elementer kan mindske træk, men de erstatter ikke god ventilation. Fugten fra bad, madlavning og daglig brug skal stadig kunne komme ud. Det er derfor vigtigt at bevare et passende luftskifte, når boligen bliver tættere efter udskiftningen.
                      </p>
                    </div>
                  </div>

                  {/* Boks 5: Montering & Faglig proces */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-hammer" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Fagligt håndværk</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Montering af vinduer og døre med styr på detaljerne
                        </h2>
                      </div>
                    </div>

                    <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                      <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
                        Vi aftaler opgaven, prisen og tidsplanen på skrift, før vi begynder. Varigheden afhænger af antal elementer, adgang og afslutninger. Ændringer i opgaven afklarer vi med dig. Skal udskiftningen indgå i en større{" "}
                        <Link
                          to="/ydelser/renovering"
                          className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
                          title="Læs om renovering af hus"
                        >
                          renovering af huset
                        </Link>
                        , eller overvejer du samtidig et{" "}
                        <Link
                          to="/ydelser/nyt-tag"
                          className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
                          title="Læs om nyt tag"
                        >
                          nyt tag
                        </Link>
                        , planlægger vi hele opgaven som en del af det samlede forløb.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
                        <i className="fa-solid fa-ruler-combined text-yellow-600" />
                        Fra opmåling til færdig montering
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-5 rounded-2xl border border-neutral-200/90 shadow-sm space-y-2">
                          <div className="font-bold text-sm text-neutral-900 flex items-center gap-2">
                            <i className="fa-solid fa-circle-check text-yellow-600" />
                            Korrekt tætning & isolering
                          </div>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Ved montering af vinduer og døre er opmåling, fastgørelse og tætning afgørende. Vi tilpasser monteringen til åbningen og følger anvisningerne for de valgte elementer. Isolering og fugning omkring karmen skal udføres korrekt, så samlingen beskytter mod træk og regn og ikke spærrer fugt inde.
                          </p>
                        </div>

                        <div className="bg-white p-5 rounded-2xl border border-neutral-200/90 shadow-sm space-y-2">
                          <div className="font-bold text-sm text-neutral-900 flex items-center gap-2">
                            <i className="fa-solid fa-circle-check text-yellow-600" />
                            Afslutninger & funktionstjek
                          </div>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Vi kontrollerer også, at de nye elementer åbner og lukker, som de skal. Indvendige lister og afslutninger omkring karmen kan indgå i opgaven, men omfanget skal aftales. Det samme gælder håndtering af de gamle elementer. Så ved du, hvad monteringen omfatter, og hvad der eventuelt skal udføres bagefter.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boks 6: Tilbud & Konkret dialog */}
                  <div className="bg-neutral-900 text-white p-7 sm:p-9 rounded-3xl shadow-xl border border-neutral-800 space-y-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-36 h-36 bg-yellow-500/10 rounded-bl-full pointer-events-none" />
                    <div className="relative z-10 space-y-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <i className="fa-solid fa-file-invoice-dollar" />
                        Gennemskuelig pris
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                        Få et tilbud på vinduer og døre til din bolig
                      </h2>
                      <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
                        Prisen på nye vinduer og døre med montering afhænger af størrelse, antal, materialer og glas. Særlige mål, vanskelig adgang og skader omkring åbningen kan også påvirke omfanget. Derfor giver et tilbud på den konkrete opgave et bedre grundlag end en generel pris pr. element.
                      </p>
                      <p className="text-neutral-200 text-sm sm:text-base leading-relaxed pt-1">
                        Skal du have skiftet vinduer og døre i Nykøbing Mors og omegn? Kontakt{" "}
                        <Link
                          to="/kontakt"
                          className="text-yellow-400 hover:text-yellow-300 font-bold underline underline-offset-2"
                          title="Kontakt JG Mesterbyg"
                        >
                          JG Mesterbyg
                        </Link>{" "}
                        for et uforpligtende tilbud. Fortæl om dine ønsker, og send gerne billeder af de eksisterende elementer. Så kan vi tage en konkret snak om dine muligheder og næste skridt.
                      </p>
                      <div className="pt-4 flex flex-col sm:flex-row gap-4">
                        <Link
                          to="/fa-tilbud"
                          className="bg-yellow-500 hover:bg-yellow-400 text-neutral-950 font-bold px-6 py-3.5 rounded-xl text-center shadow-lg transition-colors cursor-pointer inline-flex items-center justify-center gap-2"
                        >
                          <i className="fa-solid fa-calculator" />
                          Indhent et uforpligtende tilbud
                        </Link>
                        <a
                          href={`tel:${CONTACT_INFO.phone}`}
                          className="bg-neutral-800 hover:bg-neutral-700 text-white font-bold px-6 py-3.5 rounded-xl text-center border border-neutral-700 transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <i className="fa-solid fa-phone" />
                          Ring: {CONTACT_INFO.phoneDisplay}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Relaterede ydelser: Nyt tag & Renovering */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Kombiner dine projekter
                      </span>
                      <h3 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                        Overvejer du også renovering eller nyt tag?
                      </h3>
                      <p className="text-neutral-600 text-sm mt-1">
                        Når du udskifter vinduer og døre, kan det ofte betale sig at samle arbejdet med andre tømreropgaver hos os:
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Link
                        to="/ydelser/renovering"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-hammer" />
                          </div>
                          <h4 className="font-bold text-neutral-900 text-lg group-hover:text-yellow-700 transition-colors flex items-center justify-between">
                            <span>Renovering af hus</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h4>
                          <p className="text-xs sm:text-sm text-neutral-600 mt-2 leading-relaxed">
                            Skab helhed i boligen med nye lofter, gulve, rumændringer eller montering af et nyt køkken.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-yellow-600 mt-4 flex items-center gap-1.5">
                          Læs mere om renovering <i className="fa-solid fa-chevron-right text-[10px]" />
                        </span>
                      </Link>

                      <Link
                        to="/ydelser/nyt-tag"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-house-chimney" />
                          </div>
                          <h4 className="font-bold text-neutral-900 text-lg group-hover:text-yellow-700 transition-colors flex items-center justify-between">
                            <span>Nyt tag</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h4>
                          <p className="text-xs sm:text-sm text-neutral-600 mt-2 leading-relaxed">
                            Få et komplet overblik over tagmaterialer, undertag og isolering for en fuldstændig tæt klimaskærm.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-yellow-600 mt-4 flex items-center gap-1.5">
                          Læs mere om nyt tag <i className="fa-solid fa-chevron-right text-[10px]" />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : slug === "renovering" ? (
                <div className="space-y-8">
                  {/* Boks 1: Intro / Nøglebudskab */}
                  <div className="bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-800 text-white p-7 sm:p-9 rounded-3xl shadow-xl border border-neutral-700/80 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-yellow-500/10 rounded-bl-full pointer-events-none blur-xl" />
                    <div className="relative z-10 space-y-4">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <i className="fa-solid fa-hammer" />
                        <span>Professionel boligfornyelse</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                        Renovering af hus, få mere ud af din bolig
                      </h2>
                      <p className="text-neutral-200 text-base sm:text-lg leading-relaxed">
                        Hos{" "}
                        <Link
                          to="/"
                          className="text-yellow-400 hover:text-yellow-300 font-bold underline underline-offset-4 decoration-yellow-400/60 hover:decoration-yellow-300 transition-colors"
                          title="Gå til forsiden for JG Mesterbyg"
                        >
                          JG Mesterbyg
                        </Link>{" "}
                        hjælper vi med renovering af hus i Skive og omegn, fra enkelte rum til en større ombygning. Måske er køkkenet slidt, eller pladsen bliver ikke brugt godt nok. Vi tager udgangspunkt i dit hus og dine ønsker, så du får overblik over mulighederne, materialerne og arbejdet.
                      </p>
                      <div className="pt-2 flex flex-wrap gap-2 text-xs text-neutral-300">
                        <span className="bg-neutral-800/90 border border-neutral-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <i className="fa-solid fa-check text-yellow-400" /> Mors & Erslev
                        </span>
                        <span className="bg-neutral-800/90 border border-neutral-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <i className="fa-solid fa-check text-yellow-400" /> Skive & omegn
                        </span>
                        <span className="bg-neutral-800/90 border border-neutral-700 px-3 py-1.5 rounded-lg flex items-center gap-2">
                          <i className="fa-solid fa-check text-yellow-400" /> Thisted & Nykøbing Mors
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Boks 2: Hvad skal din renovering gøre bedre? */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-compass-drafting" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Planlægning & Sikkerhed</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Hvad skal din renovering gøre bedre?
                        </h2>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-1">
                      <div className="bg-white border border-neutral-200/90 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="flex items-center gap-2.5 text-neutral-900 font-bold text-base">
                          <i className="fa-solid fa-house-user text-yellow-600" />
                          <span>Mening for huset og hverdagen</span>
                        </div>
                        <p className="text-neutral-600 text-sm leading-relaxed">
                          En renovering skal både give mening for husets stand og den måde, du bor på. Vi gennemgår rumfordeling og eksisterende konstruktioner sammen med dig. Her afklarer vi, hvad der kan bevares, og hvor nye lofter, gulve eller vægge vil gøre en forskel.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200/90 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="flex items-center gap-2.5 text-neutral-900 font-bold text-base">
                          <i className="fa-solid fa-triangle-exclamation text-amber-500" />
                          <span>Bærende vægge & tilladelser</span>
                        </div>
                        <p className="text-neutral-600 text-sm leading-relaxed">
                          Vil du åbne mellem køkken og stue, skal vi først afklare, om væggen er bærende. Det gælder også ved en mindre åbning. En bærende konstruktion skal vurderes af en ingeniør, før den ændres. Eventuelle krav om byggetilladelse skal også afklares, så der er styr på rammerne, inden arbejdet begynder.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 3: Vores renoveringsopgaver (Modulære kort) */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-list-check" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Ydelser & Fagområder</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Omfattende renovering tilpasset dine behov
                        </h2>
                      </div>
                    </div>

                    <p className="text-neutral-700 text-base leading-relaxed">
                      Vi hjælper med alle faser og delopgaver i din renovering, så du får en helhedsorienteret løsning af højeste håndværksmæssige kvalitet:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
                      <div className="bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500/50 transition-all flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3">
                            <i className="fa-solid fa-layer-group" />
                          </div>
                          <h3 className="font-bold text-neutral-900 text-base mb-1.5">Lofter, gulve & døre</h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Nye lofter, gulve og indvendige døre, der giver boligen en harmonisk og frisk finish.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
                          <i className="fa-solid fa-check text-yellow-600" /> Rumfornyelse
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500/50 transition-all flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3">
                            <i className="fa-solid fa-table-columns" />
                          </div>
                          <h3 className="font-bold text-neutral-900 text-base mb-1.5">Skillevægge & rum</h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Skillevægge og ændringer af boligens rum for optimal udnyttelse af kvadratmeterne.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
                          <i className="fa-solid fa-check text-yellow-600" /> Ny rumfordeling
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500/50 transition-all flex flex-col justify-between">
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3">
                            <i className="fa-solid fa-temperature-arrow-up" />
                          </div>
                          <h3 className="font-bold text-neutral-900 text-base mb-1.5">Efterisolering</h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Efterisolering af lofter, vægge og gulve til gavn for indeklimaet og energiregningen.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500">
                          <i className="fa-solid fa-check text-yellow-600" /> Energioptimering
                        </div>
                      </div>

                      <Link
                        to="/ydelser/udskiftning-af-vinduer-og-doere"
                        className="group bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500 hover:shadow-md transition-all flex flex-col justify-between sm:col-span-1 lg:col-span-1"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-door-open" />
                          </div>
                          <h3 className="font-bold text-neutral-900 group-hover:text-yellow-700 text-base mb-1.5 flex items-center justify-between">
                            <span>Vinduer & døre</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            <span className="text-yellow-700 font-semibold underline underline-offset-2">Udskiftning af vinduer og døre</span> integreret naturligt som en del af renoveringen.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between text-[11px] font-semibold text-neutral-500">
                          <span className="flex items-center gap-1.5">
                            <i className="fa-solid fa-check text-yellow-600" /> Tæt klimaskærm
                          </span>
                          <span className="text-yellow-600 font-bold group-hover:underline">Se ydelse</span>
                        </div>
                      </Link>

                      <Link
                        to="/ydelser/nyt-tag"
                        className="group bg-white rounded-2xl p-5 border border-neutral-200 shadow-sm hover:border-yellow-500 hover:shadow-md transition-all flex flex-col justify-between sm:col-span-2 lg:col-span-2"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-800 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-house-chimney" />
                          </div>
                          <h3 className="font-bold text-neutral-900 group-hover:text-yellow-700 text-base mb-1.5 flex items-center justify-between">
                            <span>Tagarbejde & Nyt tag</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h3>
                          <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                            Tagarbejde, hvis renoveringen også kræver et <span className="text-yellow-700 font-semibold underline underline-offset-2">nyt tag</span> eller renovering af tagkonstruktionen.
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between text-[11px] font-semibold text-neutral-500">
                          <span className="flex items-center gap-1.5">
                            <i className="fa-solid fa-check text-yellow-600" /> Komplet tagløsning
                          </span>
                          <span className="text-yellow-600 font-bold group-hover:underline">Se ydelse</span>
                        </div>
                      </Link>
                    </div>
                  </div>

                  {/* Boks 4: Renovering af køkken */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-kitchen-set" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Køkkenfornyelse</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Renovering af køkken med fokus på plads og funktion
                        </h2>
                      </div>
                    </div>

                    <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                      <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
                        Ved renovering af køkken ser vi på indretning, opmåling og materialer, før det gamle køkken fjernes. Skabe og bordplader skal passe til rummet, men også til den måde, du bruger det på. Vi kan klargøre vægge og gulve og montere det nye køkken, så de forskellige opgaver hænger sammen. Læs også om vores specialiserede{" "}
                        <Link
                          to="/ydelser/koekkenrenovering"
                          className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
                          title="Læs om køkkenrenovering"
                        >
                          køkkenrenovering
                        </Link>
                        .
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-1">
                      <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-boxes-packing" />
                          </div>
                          <h3 className="text-lg sm:text-xl font-bold text-neutral-900">
                            Har du allerede købt dit nye køkken?
                          </h3>
                        </div>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Vi monterer også køkkener, som du har købt hos en anden leverandør. Arbejdet kan omfatte skabe, skuffer, bordplader og integrerede hvidevarer. En grundig opmåling er vigtig, så delene passer til rummet, og du får plads til at åbne døre og skuffer uden at ramme andre elementer.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-bolt" />
                          </div>
                          <h3 className="text-lg sm:text-xl font-bold text-neutral-900">
                            Koordinering af el og VVS
                          </h3>
                        </div>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Skal vandtilslutninger eller elinstallationer flyttes, skal det tænkes ind tidligt. Vi kan koordinere køkkenprojektet med de øvrige faggrupper. Arbejde, der kræver autorisation, udføres af autoriserede installatører. Dermed kan tømrerarbejdet og installationerne planlægges i den rette rækkefølge.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 5: Hvad koster renovering af hus? (Ensartede hvide bokse som på vinduer-siden) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-coins" />
                        </div>
                        <h2 className="text-lg sm:text-xl font-bold text-neutral-900">
                          Hvad koster renovering af hus?
                        </h2>
                      </div>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        Prisen på renovering af hus afhænger af opgavens omfang, husets stand og dit valg af materialer. Nye gulve i et enkelt rum er en anden opgave end en ombygning med ændrede vægge og installationer. Derfor skal arbejdet vurderes konkret, før vi kan give dig et tilbud, der passer til projektet.
                      </p>
                    </div>

                    <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-file-lines" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-neutral-900">
                          Skriftligt tilbud & skjulte skader
                        </h3>
                      </div>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        Vi beskriver de aftalte opgaver og materialer i et skriftligt tilbud. I et ældre hus kan der dukke skjulte skader op, når konstruktionerne åbnes. Hvis det ændrer opgaven, tager vi en dialog om det ekstra arbejde og prisen, før det udføres.
                      </p>
                    </div>
                  </div>

                  {/* Boks 6: Sådan planlægger vi din renovering */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-calendar-days" />
                      </div>
                      <div>
                        <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block">Faglig proces</span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                          Sådan planlægger vi din renovering
                        </h2>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-6 rounded-2xl border border-neutral-200/90 shadow-sm space-y-2">
                        <div className="font-bold text-base text-neutral-900 flex items-center gap-2">
                          <i className="fa-solid fa-clock text-yellow-600" />
                          Tidsplan & hverdagen under arbejdet
                        </div>
                        <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                          Tidsplanen afhænger af, hvor meget der skal ændres, og hvilke faggrupper der skal deltage. Vi planlægger rækkefølgen af arbejdet og taler med dig om adgang til rummene. Hvis du vil bo i huset under renoveringen, skal vi især afklare, hvornår køkkenet og andre vigtige rum ikke kan bruges.
                        </p>
                      </div>

                      <div className="bg-white p-6 rounded-2xl border border-neutral-200/90 shadow-sm space-y-2">
                        <div className="font-bold text-base text-neutral-900 flex items-center gap-2">
                          <i className="fa-solid fa-circle-check text-yellow-600" />
                          Fælles gennemgang & aflevering
                        </div>
                        <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                          Når arbejdet er færdigt, gennemgår vi det sammen med dig. Vi ser på montering, samlinger og de sidste detaljer. Hvis der er noget, som kræver opfølgning, bliver det noteret, så vi kan aftale, hvordan det håndteres, og du kan stille spørgsmål.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 7: Lokalt dækningsområde & Få et tilbud */}
                  <div className="bg-neutral-900 text-white p-7 sm:p-9 rounded-3xl shadow-xl border border-neutral-800 space-y-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-36 h-36 bg-yellow-500/10 rounded-bl-full pointer-events-none" />
                    <div className="relative z-10 space-y-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <i className="fa-solid fa-map-location-dot" />
                        Lokalt i Nordjylland
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                        Renovering af hus i Thisted og Nykøbing Mors samt omegn
                      </h2>
                      <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
                        Vi har base i Erslev på Mors og hjælper med renovering af hus i Nykøbing Mors og omegn. Vi kører også til Skive og Thisted samt omegn. Du kan kontakte os om både mindre opgaver og en større ombygning, der kræver koordinering af flere faggrupper.
                      </p>
                      <div className="pt-2 border-t border-neutral-800">
                        <h3 className="text-lg font-bold text-yellow-400 mb-1">Få et tilbud på din renovering</h3>
                        <p className="text-neutral-200 text-sm sm:text-base leading-relaxed">
                          Fortæl os, hvad du vil ændre, hvor huset ligger, og hvilke materialer du overvejer. Billeder eller en enkel tegning kan hjælpe os med at forstå opgaven. Kontakt{" "}
                          <Link
                            to="/kontakt"
                            className="text-yellow-400 hover:text-yellow-300 font-bold underline underline-offset-2"
                            title="Kontakt JG Mesterbyg"
                          >
                            JG Mesterbyg
                          </Link>{" "}
                          for en indledende samtale og et uforpligtende tilbud på renovering af hus eller køkken.
                        </p>
                      </div>
                      <div className="pt-4 flex flex-col sm:flex-row gap-4">
                        <Link
                          to="/fa-tilbud"
                          className="bg-yellow-500 hover:bg-yellow-400 text-neutral-950 font-bold px-6 py-3.5 rounded-xl text-center shadow-lg transition-colors cursor-pointer inline-flex items-center justify-center gap-2"
                        >
                          <i className="fa-solid fa-calculator" />
                          Indhent et uforpligtende tilbud
                        </Link>
                        <a
                          href={`tel:${CONTACT_INFO.phone}`}
                          className="bg-neutral-800 hover:bg-neutral-700 text-white font-bold px-6 py-3.5 rounded-xl text-center border border-neutral-700 transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <i className="fa-solid fa-phone" />
                          Ring: {CONTACT_INFO.phoneDisplay}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Relaterede ydelser: Vinduer & døre samt Nyt tag */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Kombiner dine projekter
                      </span>
                      <h3 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                        Overvejer du også nye vinduer eller nyt tag?
                      </h3>
                      <p className="text-neutral-600 text-sm mt-1">
                        Når du renoverer, er det ofte mest fordelagtigt at koordinere vinduer, døre og tagarbejde samtidig:
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Link
                        to="/ydelser/udskiftning-af-vinduer-og-doere"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-door-open" />
                          </div>
                          <h4 className="font-bold text-neutral-900 text-lg group-hover:text-yellow-700 transition-colors flex items-center justify-between">
                            <span>Udskiftning af vinduer og døre</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h4>
                          <p className="text-xs sm:text-sm text-neutral-600 mt-2 leading-relaxed">
                            Undgå træk og varmetab med moderne energi- og lydisolerede elementer i træ, træ/alu eller plast.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-yellow-600 mt-4 flex items-center gap-1.5">
                          Læs mere om vinduer og døre <i className="fa-solid fa-chevron-right text-[10px]" />
                        </span>
                      </Link>

                      <Link
                        to="/ydelser/nyt-tag"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-house-chimney" />
                          </div>
                          <h4 className="font-bold text-neutral-900 text-lg group-hover:text-yellow-700 transition-colors flex items-center justify-between">
                            <span>Nyt tag</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h4>
                          <p className="text-xs sm:text-sm text-neutral-600 mt-2 leading-relaxed">
                            Gennemgang af spær, undertag og lægning af nyt tag for en komplet og tæt klimaskærm.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-yellow-600 mt-4 flex items-center gap-1.5">
                          Læs mere om nyt tag <i className="fa-solid fa-chevron-right text-[10px]" />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : slug === "nyt-tag" ? (
                <div className="space-y-12">
                  {/* Boks 1: Intro / Nøglebudskab */}
                  <div className="bg-neutral-950 text-white rounded-3xl p-8 sm:p-10 shadow-xl border border-neutral-800 space-y-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="relative z-10 space-y-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <i className="fa-solid fa-house-chimney text-xs" />
                        Kvalitetstag til din bolig
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                        Nyt tag med overblik over pris og forløb
                      </h2>
                      <p className="text-neutral-300 text-base sm:text-lg leading-relaxed max-w-3xl">
                        Skal dit slidte eller utætte tag skiftes? Hos{" "}
                        <Link
                          to="/"
                          className="text-yellow-400 hover:text-yellow-300 font-bold underline underline-offset-4 decoration-yellow-400/60 hover:decoration-yellow-300 transition-colors"
                          title="Gå til forsiden for JG Mesterbyg"
                        >
                          JG Mesterbyg
                        </Link>{" "}
                        hjælper vi med nyt tag i Nordjylland og omegn samt i Thisted, Skive og Nykøbing Mors med omegn. Fra vores base i Erslev på Mors rådgiver vi om materialer, pris og forløb, så du kan træffe dit valg på et klart grundlag.
                      </p>
                    </div>

                    <div className="relative z-10 pt-3 border-t border-neutral-800 flex flex-wrap gap-2 text-xs font-medium text-neutral-400">
                      <span className="px-3 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-300 flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-yellow-500" /> Mors & Erslev
                      </span>
                      <span className="px-3 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-300 flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-yellow-500" /> Skive & omegn
                      </span>
                      <span className="px-3 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-300 flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-yellow-500" /> Thisted & omegn
                      </span>
                      <span className="px-3 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-300 flex items-center gap-1.5">
                        <i className="fa-solid fa-location-dot text-yellow-500" /> Nykøbing Mors
                      </span>
                    </div>
                  </div>

                  {/* Boks 2: Skal taget skiftes eller kan det repareres? */}
                  <div className="space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Tilstandsvurdering & Bærende konstruktion
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-950 tracking-tight pb-3 border-b-2 border-yellow-500/20 relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-16 after:h-[2px] after:bg-yellow-500">
                        Skal taget skiftes eller kan det repareres?
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-triangle-exclamation" />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-950">
                          Vurdering af skader og tegn på slid
                        </h3>
                        <p className="text-neutral-700 text-base leading-relaxed">
                          Revner, løse tagsten og gentagne utætheder kan være tegn på, at taget trænger til mere end en reparation. Det samme gælder fugt på loftet eller tydeligt slid på tagfladen. Vi hjælper dig med at vurdere skaderne og finde ud af, om hele taget skal skiftes.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-magnifying-glass" />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-950">
                          Gennemgang af spær & konstruktion
                        </h3>
                        <p className="text-neutral-700 text-base leading-relaxed">
                          Vi gennemgår tagbelægningen og de bærende spær, som holder taget oppe. Her ser vi på, om konstruktionen kan bære den nye tagtype, og om den skal udbedres eller forstærkes. Gennemgangen danner grundlag for vores råd om reparation eller udskiftning af dit tag.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 3: Hvad er prisen på et nyt tag? */}
                  <div className="space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Gennemsigtighed & Indhold i tilbuddet
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-950 tracking-tight pb-3 border-b-2 border-yellow-500/20 relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-16 after:h-[2px] after:bg-yellow-500">
                        Hvad er prisen på et nyt tag?
                      </h2>
                    </div>

                    <p className="text-neutral-700 text-base sm:text-lg leading-relaxed">
                      Prisen på et nyt tag afhænger af tagets størrelse, form og materialer. Kviste, skorstene og adgangsforhold kan også påvirke arbejdets omfang. En pris pr. kvadratmeter giver derfor ikke hele billedet. I dit tilbud afklarer vi, hvilke af følgende poster der indgår.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                      <div className="bg-white border border-neutral-200 rounded-2xl p-5 shadow-sm hover:border-yellow-500/40 transition-all flex flex-col justify-between">
                        <div className="space-y-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-trash-can" />
                          </div>
                          <h4 className="font-bold text-neutral-950 text-base">Nedtagning & bortskaffelse</h4>
                          <p className="text-sm text-neutral-600 leading-relaxed">
                            Nedtagning af den gamle tagbelægning og bortskaffelse
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-yellow-700 mt-4 block">Nedrivning & affald</span>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-5 shadow-sm hover:border-yellow-500/40 transition-all flex flex-col justify-between">
                        <div className="space-y-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-stairs" />
                          </div>
                          <h4 className="font-bold text-neutral-950 text-base">Stillads & adgang</h4>
                          <p className="text-sm text-neutral-600 leading-relaxed">
                            Stillads og adgang til tagets flader
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-yellow-700 mt-4 block">Sikkerhed & adgang</span>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-5 shadow-sm hover:border-yellow-500/40 transition-all flex flex-col justify-between">
                        <div className="space-y-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-cubes-stacked" />
                          </div>
                          <h4 className="font-bold text-neutral-950 text-base">Spær, undertag & lægter</h4>
                          <p className="text-sm text-neutral-600 leading-relaxed">
                            Udbedring af spær samt undertag og lægter
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-yellow-700 mt-4 block">Bærende underlag</span>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-5 shadow-sm hover:border-yellow-500/40 transition-all flex flex-col justify-between">
                        <div className="space-y-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-house-chimney" />
                          </div>
                          <h4 className="font-bold text-neutral-950 text-base">Tagmaterialer & lægning</h4>
                          <p className="text-sm text-neutral-600 leading-relaxed">
                            Tagmaterialer og selve taglægningen
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-yellow-700 mt-4 block">Kvalitetsbelægning</span>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-5 shadow-sm hover:border-yellow-500/40 transition-all flex flex-col justify-between sm:col-span-2 lg:col-span-2">
                        <div className="space-y-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                            <i className="fa-solid fa-temperature-arrow-up" />
                          </div>
                          <h4 className="font-bold text-neutral-950 text-base">Efterisolering, tagrender & stern</h4>
                          <p className="text-sm text-neutral-600 leading-relaxed">
                            Eventuel efterisolering, tagrender og sternbrædder
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-yellow-700 mt-4 block">Afslutninger & optimering</span>
                      </div>
                    </div>

                    {/* Asbest-afklaring OBS-kort */}
                    <div className="bg-amber-50/70 border border-amber-200 rounded-3xl p-6 sm:p-7 shadow-sm flex items-start gap-5">
                      <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0 mt-0.5">
                        <i className="fa-solid fa-triangle-exclamation" />
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-bold text-neutral-950 text-lg">
                          Vigtig afklaring ved ældre eternittag (asbest)
                        </h4>
                        <p className="text-neutral-700 text-base leading-relaxed">
                          Har dit hus et ældre eternittag, skal det afklares, om pladerne indeholder asbest. Det kan få betydning for både pris og planlægning. Et asbestholdigt tag skal som udgangspunkt nedtages af en autoriseret virksomhed, og reglerne for bortskaffelse skal også tænkes med. Skal tagarbejdet indgå som del af en generel{" "}
                          <Link
                            to="/ydelser/renovering"
                            className="text-yellow-700 hover:text-yellow-800 font-bold underline underline-offset-2"
                            title="Læs om renovering af hus"
                          >
                            renovering af huset
                          </Link>
                          , afstemmer vi stillads og tidsplan for det mest økonomiske forløb.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 4: Hvilket tagmateriale passer til dit hus? */}
                  <div className="space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Materialer & Egenskaber
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-950 tracking-tight pb-3 border-b-2 border-yellow-500/20 relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-16 after:h-[2px] after:bg-yellow-500">
                        Hvilket tagmateriale passer til dit hus?
                      </h2>
                    </div>

                    <p className="text-neutral-700 text-base sm:text-lg leading-relaxed">
                      Vi arbejder med tegl, betontagsten, bølgeplader af fibercement, skifer og stål. Valget afhænger af tagets hældning, spærenes bæreevne og husets udtryk. Vi forklarer forskellene, så du kan vælge en belægning, der passer til huset og dit behov for vedligeholdelse.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                      <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-cubes" />
                        </div>
                        <h4 className="font-bold text-neutral-950 text-lg">Tegl & betontagsten</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Klassisk og holdbar løsning, der tilfører huset et flot arkitektonisk udtryk med lang levetid.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-bars-progress" />
                        </div>
                        <h4 className="font-bold text-neutral-950 text-lg">Bølgeplader af fibercement</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Robust, gennemprøvet og økonomisk tagtype, der egner sig godt til både parcelhuse og landejendomme.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-gem" />
                        </div>
                        <h4 className="font-bold text-neutral-950 text-lg">Skifertag</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Eksklusivt, stilrent og tidløst udtryk med ekstremt lang holdbarhed og minimal vedligeholdelse.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm space-y-3">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-shield" />
                        </div>
                        <h4 className="font-bold text-neutral-950 text-lg">Ståltag</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Let, stærk og hurtigt monteret løsning, der stiller færre krav til spærenes bæreevne.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm space-y-3 md:col-span-2 lg:col-span-2">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-clipboard-check" />
                        </div>
                        <h4 className="font-bold text-neutral-950 text-lg">Spær, undertag & lokalplan</h4>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          Spærene skal kunne bære den valgte belægning, og hældningen skal passe til materialet. Vi ser også på behovet for undertag, som beskytter mod vand under selve tagbelægningen. Der kan desuden være krav til tagets udseende i en lokalplan. De bør afklares, inden vi aftaler den nye løsning.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 5: Sådan foregår montering af nyt tag */}
                  <div className="space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Arbejdsproces & Montering
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-950 tracking-tight pb-3 border-b-2 border-yellow-500/20 relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-16 after:h-[2px] after:bg-yellow-500">
                        Sådan foregår montering af nyt tag
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-cloud-sun-rain" />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-950">
                          Tidsplan, adgang & vejrbeskyttelse
                        </h3>
                        <p className="text-neutral-700 text-base leading-relaxed">
                          Når vi har aftalt opgaven, lægger vi en tidsplan sammen med dig. Ved en tagudskiftning skal den gamle belægning fjernes, og delene under den skal gennemgås. Vi taler også om adgang til huset og beskyttelse mod vejret, så du ved, hvad arbejdet betyder for din hverdag.
                        </p>
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm space-y-4">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg shrink-0">
                          <i className="fa-solid fa-hammer" />
                        </div>
                        <h3 className="text-xl font-bold text-neutral-950">
                          Undertag, taglægning & efterisolering
                        </h3>
                        <p className="text-neutral-700 text-base leading-relaxed">
                          Vi monterer undertag og lægter, hvor løsningen kræver det, og lægger den nye tagbelægning. Tagrender og sternbrædder indgår efter aftale. Vi vurderer også, om loftet bør efterisoleres. Det kan mindske varmetabet, men kræver, at isolering og ventilation passer sammen. Mange vælger i samme ombæring{" "}
                          <Link
                            to="/ydelser/udskiftning-af-vinduer-og-doere"
                            className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
                            title="Læs om udskiftning af vinduer og døre"
                          >
                            udskiftning af vinduer og døre
                          </Link>{" "}
                          for at opnå en komplet forsegling af klimaskærmen. Den mulige gevinst afhænger af husets stand.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Boks 6: Nyt tag i Thisted, Skive og Nykøbing Mors med omegn */}
                  <div className="space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Lokale tagprojekter
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-950 tracking-tight pb-3 border-b-2 border-yellow-500/20 relative after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-16 after:h-[2px] after:bg-yellow-500">
                        Nyt tag i Thisted, Skive og Nykøbing Mors med omegn
                      </h2>
                    </div>

                    <p className="text-neutral-700 text-base sm:text-lg leading-relaxed">
                      Fra vores base i Erslev på Mors tager vi tagopgaver i Nordjylland og omegn. Du behøver ikke kende den rette tagtype, når du kontakter os. Vi begynder med dit hus og dine ønsker. Læs mere om vores tømrerarbejde i de områder, hvor vi hjælper med nyt tag:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                      <Link
                        to="/kontakt"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-5 shadow-sm transition-all flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-base shrink-0 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-location-dot" />
                          </div>
                          <div>
                            <span className="font-bold text-neutral-900 group-hover:text-yellow-700 transition-colors block text-sm">
                              Nyt tag i Thisted og omegn
                            </span>
                            <span className="text-xs text-neutral-500">Thy & opland</span>
                          </div>
                        </div>
                        <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                      </Link>

                      <Link
                        to="/kontakt"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-5 shadow-sm transition-all flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-base shrink-0 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-location-dot" />
                          </div>
                          <div>
                            <span className="font-bold text-neutral-900 group-hover:text-yellow-700 transition-colors block text-sm">
                              Nyt tag i Skive og omegn
                            </span>
                            <span className="text-xs text-neutral-500">Salling & Skive</span>
                          </div>
                        </div>
                        <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                      </Link>

                      <Link
                        to="/kontakt"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-5 shadow-sm transition-all flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-base shrink-0 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-location-dot" />
                          </div>
                          <div>
                            <span className="font-bold text-neutral-900 group-hover:text-yellow-700 transition-colors block text-sm">
                              Nyt tag i Nykøbing Mors og omegn
                            </span>
                            <span className="text-xs text-neutral-500">Hele Mors</span>
                          </div>
                        </div>
                        <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                      </Link>
                    </div>
                  </div>

                  {/* Boks 7: Få et tilbud på nyt tag og kom videre med dit projekt */}
                  <div className="bg-neutral-950 text-white rounded-3xl p-8 sm:p-10 shadow-xl border border-neutral-800 space-y-6 relative overflow-hidden">
                    <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="relative z-10 space-y-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <i className="fa-solid fa-handshake text-xs" />
                        Gennemskueligt & uforpligtende
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                        Få et tilbud på nyt tag og kom videre med dit projekt
                      </h3>
                      <p className="text-neutral-300 text-base leading-relaxed max-w-3xl">
                        Et tilbud på nyt tag skal give dig overblik over materialer og arbejde. Vi beskriver opgaven skriftligt og aftaler pris og tidsplan, før arbejdet går i gang. Hvis gennemgangen viser behov for udbedringer, taler vi med dig om dem, så du kan tage stilling til det samlede omfang.
                      </p>
                      <p className="text-neutral-300 text-base leading-relaxed max-w-3xl">
                        Send os gerne din adresse, billeder af taget og lidt om dine ønsker. Det er et godt udgangspunkt for den første samtale, men erstatter ikke en vurdering af taget. Kontakt{" "}
                        <Link
                          to="/kontakt"
                          className="text-yellow-400 hover:text-yellow-300 font-bold underline underline-offset-2"
                          title="Kontakt JG Mesterbyg"
                        >
                          JG Mesterbyg
                        </Link>{" "}
                        og få et uforpligtende tilbud på en løsning, der tager højde for dit hus.
                      </p>
                    </div>

                    <div className="relative z-10 pt-4 border-t border-neutral-800 flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/fa-tilbud"
                        className="bg-yellow-500 hover:bg-yellow-400 text-neutral-950 font-bold px-6 py-3.5 rounded-xl text-center shadow-lg transition-colors inline-flex items-center justify-center gap-2"
                      >
                        <i className="fa-solid fa-calculator" />
                        Indhent et uforpligtende tilbud
                      </Link>
                      <a
                        href={`tel:${CONTACT_INFO.phone}`}
                        className="bg-neutral-800 hover:bg-neutral-700 text-white font-bold px-6 py-3.5 rounded-xl text-center border border-neutral-700 transition-colors inline-flex items-center justify-center gap-2"
                      >
                        <i className="fa-solid fa-phone" />
                        Ring: {CONTACT_INFO.phoneDisplay}
                      </a>
                    </div>
                  </div>

                  {/* Relaterede ydelser: Renovering & Vinduer/Døre */}
                  <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm space-y-6">
                    <div>
                      <span className="text-yellow-600 font-bold text-xs uppercase tracking-wider block mb-1">
                        Kombiner dine projekter
                      </span>
                      <h3 className="text-xl sm:text-2xl font-extrabold text-neutral-900 tracking-tight">
                        Overvejer du også renovering eller nye vinduer og døre?
                      </h3>
                      <p className="text-neutral-600 text-sm mt-1">
                        Ved tagudskiftning er det oplagt at koordinere boligens samlede tæthed, energioptimering og rumfordeling:
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Link
                        to="/ydelser/renovering"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-hammer" />
                          </div>
                          <h4 className="font-bold text-neutral-900 text-lg group-hover:text-yellow-700 transition-colors flex items-center justify-between">
                            <span>Renovering af hus</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h4>
                          <p className="text-xs sm:text-sm text-neutral-600 mt-2 leading-relaxed">
                            Få fornyet boligen indvendigt med nye lofter, gulve, vægge og køkken samtidig med tagarbejdet.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-yellow-600 mt-4 flex items-center gap-1.5">
                          Læs mere om renovering <i className="fa-solid fa-chevron-right text-[10px]" />
                        </span>
                      </Link>

                      <Link
                        to="/ydelser/udskiftning-af-vinduer-og-doere"
                        className="group bg-white border border-neutral-200 hover:border-yellow-500 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="w-10 h-10 rounded-xl bg-yellow-500/20 text-yellow-700 flex items-center justify-center text-lg mb-3 group-hover:bg-yellow-500 group-hover:text-black transition-colors">
                            <i className="fa-solid fa-door-open" />
                          </div>
                          <h4 className="font-bold text-neutral-900 text-lg group-hover:text-yellow-700 transition-colors flex items-center justify-between">
                            <span>Udskiftning af vinduer og døre</span>
                            <i className="fa-solid fa-arrow-right text-xs text-neutral-400 group-hover:text-yellow-600 group-hover:translate-x-1 transition-all" />
                          </h4>
                          <p className="text-xs sm:text-sm text-neutral-600 mt-2 leading-relaxed">
                            Komplet forsegling af klimaskærmen med nye energirigtige vinduer og facadedøre.
                          </p>
                        </div>
                        <span className="text-xs font-bold text-yellow-600 mt-4 flex items-center gap-1.5">
                          Læs mere om vinduer og døre <i className="fa-solid fa-chevron-right text-[10px]" />
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  className="service-prose space-y-6 text-neutral-700 leading-relaxed 
                    [&_a]:text-yellow-600 [&_a]:font-bold [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-yellow-700
                    [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-extrabold [&_h2]:text-neutral-950 [&_h2]:mt-12 [&_h2]:mb-5 [&_h2]:pb-3 [&_h2]:tracking-tight [&_h2]:border-b-2 [&_h2]:border-yellow-500/20 [&_h2]:relative [&_h2]:after:content-[''] [&_h2]:after:absolute [&_h2]:after:bottom-[-2px] [&_h2]:after:left-0 [&_h2]:after:w-16 [&_h2]:after:h-[2px] [&_h2]:after:bg-yellow-500
                    [&_h3]:text-xl [&_h3]:sm:text-2xl [&_h3]:font-bold [&_h3]:text-neutral-900 [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:flex [&_h3]:items-center [&_h3]:gap-3
                    [&_p]:text-neutral-700 [&_p]:leading-relaxed [&_p]:mb-5 [&_p]:text-base sm:[&_p]:text-[17px]
                    [&_ul]:my-8 [&_ul]:grid [&_ul]:grid-cols-1 md:[&_ul]:grid-cols-3 [&_ul]:gap-4 [&_ul]:p-0 [&_ul]:list-none
                    [&_ul_li]:relative [&_ul_li]:bg-neutral-50 [&_ul_li]:hover:bg-amber-50/50 [&_ul_li]:border [&_ul_li]:border-neutral-200 [&_ul_li]:hover:border-yellow-500/40 [&_ul_li]:rounded-2xl [&_ul_li]:p-5 [&_ul_li]:text-neutral-800 [&_ul_li]:shadow-sm [&_ul_li]:transition-all [&_ul_li]:duration-200 [&_ul_li]:before:content-none [&_ul_li]:flex [&_ul_li]:flex-col [&_ul_li]:justify-start
                    [&_ul_li_strong]:text-yellow-600 [&_ul_li_strong]:text-lg [&_ul_li_strong]:font-black [&_ul_li_strong]:mb-2 [&_ul_li_strong]:block
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-3 [&_ol]:my-6 [&_ol]:text-neutral-700
                    [&_strong]:font-bold [&_strong]:text-neutral-950 
                    [&_blockquote]:my-8 [&_blockquote]:bg-gradient-to-br [&_blockquote]:from-amber-500/10 [&_blockquote]:via-yellow-500/5 [&_blockquote]:to-transparent [&_blockquote]:border-l-4 [&_blockquote]:border-yellow-500 [&_blockquote]:rounded-r-2xl [&_blockquote]:p-6 sm:[&_blockquote]:p-7 [&_blockquote]:text-neutral-900 [&_blockquote]:shadow-sm [&_blockquote]:relative
                    [&_blockquote_p]:!mb-0 [&_blockquote_p]:font-medium [&_blockquote_p]:text-neutral-800"
                  dangerouslySetInnerHTML={{ __html: service.content }}
                />
              )}

              {/* Gallery Section */}
              {gallery && gallery.images && gallery.images.length > 0 && (
                <div className="space-y-6 pt-8 border-t border-neutral-200">
                  <div>
                    <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-1">Vores arbejde</span>
                    <h2 className="text-2xl font-extrabold text-neutral-900">
                      Billeder fra udførte opgaver
                    </h2>
                    <p className="text-sm text-neutral-500 mt-1">
                      Klik på et billede for at se det i fuld størrelse.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {gallery.images.map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => setActiveImage(img)}
                        className="relative h-48 rounded-xl overflow-hidden border border-neutral-200 shadow-sm cursor-pointer group"
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/${img}`}
                          alt={getImageAlt(img, `${service.title} reference`)}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-28">
              {/* Sidebar CTA Box */}
              <div className="bg-neutral-900 text-white p-8 rounded-2xl shadow-xl border border-neutral-800 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-500/10 rounded-bl-full pointer-events-none" />
                <h3 className="text-xl font-bold mb-4">Skal vi hjælpe dig?</h3>
                <p className="text-sm text-neutral-300 leading-relaxed mb-6">
                  Få et uforpligtende tilbud på {service.title.toLowerCase()} tilpasset dine specifikke behov og ønsker.
                </p>
                <div className="space-y-4">
                  <Link
                    to="/fa-tilbud"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold py-3 px-4 rounded-xl text-center block shadow-md transition-all duration-200 cursor-pointer"
                  >
                    Få et tilbud
                  </Link>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="w-full bg-neutral-800 hover:bg-neutral-700 text-white font-bold py-3 px-4 rounded-xl text-center block border border-neutral-700 transition-all duration-200"
                  >
                    <i className="fa-solid fa-phone mr-2" />
                    Ring os op
                  </a>
                </div>
              </div>

              {/* Vores ydelser navigation */}
              <div className="bg-neutral-50 p-6 rounded-2xl border border-neutral-200 space-y-3">
                <h4 className="font-bold text-neutral-900 text-sm uppercase tracking-wider flex items-center gap-2">
                  <i className="fa-solid fa-list-check text-yellow-600" />
                  Vores ydelser
                </h4>
                <div className="space-y-1.5 pt-1">
                  {[
                    { slug: "udskiftning-af-vinduer-og-doere", title: "Udskiftning af vinduer og døre", icon: "fa-door-open" },
                    { slug: "renovering", title: "Renovering", icon: "fa-hammer" },
                    { slug: "nyt-tag", title: "Nyt tag", icon: "fa-house-chimney" },
                    { slug: "koekkenrenovering", title: "Køkkenrenovering", icon: "fa-kitchen-set" },
                    { slug: "opsaetning-af-solceller", title: "Opsætning af solceller", icon: "fa-solar-panel" },
                  ].map((item) => (
                    <Link
                      key={item.slug}
                      to={`/ydelser/${item.slug}`}
                      className={`flex items-center justify-between p-2.5 rounded-xl text-xs font-semibold transition-all ${
                        slug === item.slug
                          ? "bg-yellow-500 text-neutral-950 shadow-sm font-bold"
                          : "bg-white text-neutral-700 hover:text-yellow-700 hover:border-yellow-400 border border-neutral-200"
                      }`}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <i className={`fa-solid ${item.icon} text-xs ${slug === item.slug ? "text-neutral-950" : "text-yellow-600"}`} />
                        <span className="truncate">{item.title}</span>
                      </span>
                      <i className={`fa-solid fa-chevron-right text-[10px] ${slug === item.slug ? "text-neutral-950" : "text-neutral-400"}`} />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Sidebar Trust Badges */}
              <div className="bg-neutral-50 p-6 rounded-2xl border border-neutral-200 space-y-4">
                <h4 className="font-bold text-neutral-900 text-sm uppercase tracking-wider">Din tryghed</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-yellow-600 mt-1" />
                    <p className="text-xs text-neutral-600">
                      <strong>Dansk Byggeri Garanti:</strong> Vores arbejde er fuldt dækket af garantiordning.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-yellow-600 mt-1" />
                    <p className="text-xs text-neutral-600">
                      <strong>Erfarne fagmænd:</strong> Uddannede danske håndværkere med mange års erfaring.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-yellow-600 mt-1" />
                    <p className="text-xs text-neutral-600">
                      <strong>Gennemsigtighed:</strong> Fast pris og skriftlig aftale inden arbejdet påbegyndes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Coverage / Lokalområder */}
              <div className="bg-neutral-50 p-6 rounded-2xl border border-neutral-200 space-y-4">
                <h4 className="font-bold text-neutral-900 text-sm uppercase tracking-wider flex items-center gap-2">
                  <i className="fa-solid fa-map-location-dot text-yellow-600" />
                  Dækningsområde
                </h4>
                <p className="text-xs text-neutral-600 leading-relaxed">
                  Vi kører ud fra vores base i Erslev på Mors og dækker hele lokalområdet:
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs font-medium text-neutral-800">
                  <div className="flex items-center gap-1.5 bg-white p-2 rounded-lg border border-neutral-200">
                    <i className="fa-solid fa-check text-yellow-600 text-[10px]" /> Mors & Erslev
                  </div>
                  <div className="flex items-center gap-1.5 bg-white p-2 rounded-lg border border-neutral-200">
                    <i className="fa-solid fa-check text-yellow-600 text-[10px]" /> Skive & omegn
                  </div>
                  <div className="flex items-center gap-1.5 bg-white p-2 rounded-lg border border-neutral-200">
                    <i className="fa-solid fa-check text-yellow-600 text-[10px]" /> Thisted & omegn
                  </div>
                  <div className="flex items-center gap-1.5 bg-white p-2 rounded-lg border border-neutral-200">
                    <i className="fa-solid fa-check text-yellow-600 text-[10px]" /> Nykøbing Mors
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-neutral-100 text-neutral-900 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-2">Kundeanmeldelser</span>
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Det siger vores kunder om os
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex gap-1 text-yellow-500 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star" />
                    ))}
                  </div>
                  <p className="text-neutral-600 italic leading-relaxed text-sm mb-6">"{t.text}"</p>
                </div>
                <div className="border-t border-neutral-100 pt-4 flex items-center justify-between">
                  <span className="font-bold text-neutral-900 text-sm">{t.name}</span>
                  <span className="text-xs text-neutral-500">{t.city}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl hover:text-yellow-500 cursor-pointer"
            onClick={() => setActiveImage(null)}
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="max-w-5xl max-h-[85vh] overflow-hidden rounded-xl border border-neutral-800 shadow-2xl">
            <img
              src={`https://assets.auxodev.dk/assets/${activeImage.split("/assets/")[1] || activeImage}`}
              alt={getImageAlt(activeImage, `${service?.title || "JG Mesterbyg"} reference`)}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
